<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="toggleDialog"
    max-width="1200px"
    scrollable
  >
    <v-card>
      <v-card-item class="pa-0">
        <v-icon icon="mdi-github" size="26" />
        <span class='workflow-from-code-title'>Workflow from Code</span>
        <v-btn
          icon="mdi-close"
          variant="plain"
          class="ml-auto"
          @click="toggleDialog(false)"
        />
      </v-card-item>
      <v-card-text class="workflow-from-code-card">
        <div v-if="!fileTree.length">
          <v-sheet
            border="dashed"
            rounded
            class="pa-8 text-center"
          >
            <p class="mb-6 text-body-1 text-medium-emphasis">
              Choose an account, repository, and branch to scan for files.
            </p>
            <connect-github
              v-model:selectedAccount="selectedAccount"
              v-model:selectedRepo="selectedRepo"
              v-model:selectedBranch="selectedBranch"
              hideButtons
              class="mb-6"
            />
            <v-btn
              color="primary"
              size="large"
              :loading="isFetchingFiles"
              @click="fetchFilesAndShowTree"
              :disabled="!selectedRepo || !selectedBranch"
              prepend-icon="mdi-arrow-down-circle-outline"
            >
              Fetch Repository Files
            </v-btn>
          </v-sheet>
        </div>

        <div v-if="fileTree.length > 0">
          <div class="d-flex align-center mb-4 pa-1">
            <v-icon color="grey-darken-1">mdi-github</v-icon>
            <span class="mx-2 text-disabled">/</span>
            <span class="font-weight-medium text-grey-darken-3">{{ selectedRepo?.owner?.login }}</span>
            <span class="mx-2 text-disabled">/</span>
            <span class="font-weight-medium text-grey-darken-3">{{ selectedRepo?.name }}</span>
            <span class="mx-2 text-disabled">/</span>
            <v-icon color="grey-darken-1">mdi-source-branch</v-icon>
            <span class="font-weight-medium ml-1 text-grey-darken-3">{{ selectedBranch }}</span>
            <v-spacer></v-spacer>
            <v-btn
              variant="outlined"
              size="small"
              @click="resetState"
              class="text-none text-medium-emphasis"
              prepend-icon="mdi-arrow-left"
            >
              Change Repository
            </v-btn>
          </div>
          <v-textarea
            v-if="!store.state.isWorkflowDisabled"
            id="ai-description"
            label="Optional Context for AI"
            auto-grow
            rows="3"
            class="no-border"
            variant="outlined"
            v-model="description"
            placeholder="e.g., This is an e-commerce backend. Focus on the order fulfillment and payment processing logic. The core entities are 'Order' and 'Shipment'."
          />
        </div>

        <v-row v-if="fileTree.length > 0" class="mt-2">
          <v-col cols="6">
            <div class="file-tree-container">
              <v-toolbar density="compact" flat color="grey-lighten-4" class="rounded-t">
                <v-toolbar-title class="text-subtitle-2">Select files to process</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn size="small" @click="selectAllFiles(true)" class="mr-1">Select All</v-btn>
                <v-btn size="small" @click="selectAllFiles(false)">Deselect All</v-btn>
              </v-toolbar>
              <div class="d-flex justify-space-between align-center pa-2 text-caption border-x">
                <span>Selected Files: {{ selectedFilePaths.length }}</span>
                <span :class="{ 'text-error font-weight-bold': isSizeLimitExceeded }">
                  Selected Size: {{ formatBytes(totalSelectedSize) }} / {{ formatBytes(MAX_TOTAL_SIZE) }}
                  <span v-if="isSizeLimitExceeded" class="ml-2">(Limit Exceeded)</span>
                </span>
              </div>
              <v-list lines="one" density="compact" class="file-tree-list border rounded-b">
                <file-tree-node
                  v-for="node in fileTree"
                  :key="node.id"
                  :node="node"
                  @selection-changed="updateTotalSelectedSize"
                />
              </v-list>
            </div>
          </v-col>

          <v-col cols="6">
            <selected-file-tree :filePaths="selectedFilePaths" />
          </v-col>
        </v-row>
        <div v-if="!isFetchingFiles && initialFetchDone && fileTree.length === 0" class="text-center pa-4">
          <p class="text-caption">No files found in the repository or it might be empty.</p>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="outlined" size="large" @click="toggleDialog(false)">Cancel</v-btn>
        <v-btn
          id="generate-workflow-from-code-btn"
          variant="flat"
          color="primary"
          size="large"
          @click="processSelectedFiles"
          :disabled="!hasSelectedFiles || isProcessingWorkflow || isSizeLimitExceeded"
          :loading="isProcessingWorkflow"
        >
          Generate
        </v-btn>
      </v-card-actions>
    </v-card>
    <workflow-from-code-components
      :modelValue="openReviewDialog"
      @update:modelValue="(val) => openReviewDialog = val"
      :extractedData="extractedData"
      @confirm="onConfirm"
    />
  </v-dialog>
</template>

<script setup>
import { useStore } from 'vuex'
import { getCurrentInstance, ref, computed, nextTick } from 'vue'
import FileTreeNode from '@/components/FileTreeNode.vue'
import SelectedFileTree from '@/components/FileTree.vue'
import ConnectGithub from '@/components/ConnectGithub.vue'
import WorkflowFromCodeComponents from '@/components/WorkflowFromCodeComponents.vue'
import gitHubApi from '@/api/gitHubApi'
import { formatBytes } from '@/utils/formatBytes'
import { createFileBatches } from '@/utils/batching'
import { buildFileTree, sortTree } from '@/utils/fileTree'
import { isFileRelevantForAnalysis } from '@/utils/fileContent'
import useGenerateWorkflow from '@/composables/useGenerateWorkflow'

const { modelValue } = defineProps({ modelValue: Boolean })
const emit = defineEmits(['update:modelValue'])

const selectedAccount = ref(null)
const selectedRepo = ref(null)
const selectedBranch = ref(null)

const description = ref('')
const repoItems = ref([])
const fileTree = ref([])
const isFetchingFiles = ref(false)
const initialFetchDone = ref(false)
const isProcessingWorkflow = ref(false)

const openReviewDialog = ref(false)
const extractedData = ref(null)
const totalSelectedSize = ref(0)
const selectedFilesWithContent = ref([])

const store = useStore()
const { proxy } = getCurrentInstance()
const { buildWorkflowFromCode, createWorkflow } = useGenerateWorkflow()

const MAX_TOTAL_SIZE = 10 * 1024 * 1024
const hasSelectedFiles = computed(() => checkSelection(fileTree.value))
const selectedFilePaths = computed(() => getSelectedFilePathsRecursive(fileTree.value))
const isSizeLimitExceeded = computed(() => totalSelectedSize.value > MAX_TOTAL_SIZE)

const toggleDialog = (value) => {
  if (!value) {
    resetState()
  }
  emit('update:modelValue', value)
}

const getSelectedFilePathsRecursive = (nodes) => {
  let selected = []
  nodes.forEach((node) => {
    if (node.type === 'blob' && node.selected) {
      selected.push(node.path)
    }
    if (node.type === 'tree' && node.children) {
      selected = selected.concat(getSelectedFilePathsRecursive(node.children))
    }
  })
  return selected
}

const fetchFilesAndShowTree = async () => {
  if (!selectedRepo.value) {
    return
  }

  const login = selectedAccount.value.login
  const owner = selectedRepo.value.owner.login
  const repo = selectedRepo.value.name
  const branch = selectedBranch.value || undefined

  isFetchingFiles.value = true
  initialFetchDone.value = false
  fileTree.value = []
  try {
    const filesResponse = await gitHubApi.listGitHubRepoFiles(login, owner, repo, null, branch)

    if (!filesResponse?.success) {
      return
    }

    repoItems.value = filesResponse.resData || []

    const rawTree = buildFileTree(repoItems.value)
    fileTree.value = sortTree(rawTree)
  } catch (error) {
    store.commit('snackbar/showMessage', {
      content: `Failed to fetch file list: ${error.message}`,
      color: 'error'
    }, { root: true })
  } finally {
    isFetchingFiles.value = false
    initialFetchDone.value = true
    updateTotalSelectedSize()
  }
}

const calculateSizeRecursive = (nodes) => {
  let total = 0
  if (!nodes) return 0

  nodes.forEach(node => {
    if (node.type === 'blob' && node.selected) {
      total += node.size || 0
    }
    if (node.type === 'tree' && node.children) {
      total += calculateSizeRecursive(node.children)
    }
  })

  return total
}

const updateTotalSelectedSize = () => {
  totalSelectedSize.value = calculateSizeRecursive(fileTree.value)
}

const getSelectedFileObjectsRecursive = (nodes) => {
  let selected = []
  nodes.forEach((node) => {
    if (node.type === 'blob' && node.selected) {
      selected.push({ path: node.path, name: node.name, content: node.content, size: node.size })
    }
    if (node.type === 'tree' && node.children) {
      selected = selected.concat(getSelectedFileObjectsRecursive(node.children))
    }
  })

  return selected
}

const checkSelection = (nodes) => {
  if (!nodes) {
    return false
  }

  for (const node of nodes) {
    if (node.type === 'blob' && node.selected) {
      return true
    }
    if (node.type === 'tree' && checkSelection(node.children)) {
      return true
    }
  }

  return false
}

const fetchFileContents = async (filesToFetch, repoInfo) => {
  if (!filesToFetch || filesToFetch.length === 0) {
    return []
  }

  const fileBatches = createFileBatches(filesToFetch)
  const batchPromises = fileBatches.map((batch) =>
    gitHubApi.fetchGitHubRepoFileContent(
      repoInfo.login,
      repoInfo.owner,
      repoInfo.repoName,
      repoInfo.branch,
      batch
    )
  )

  const allResponses = await Promise.all(batchPromises)
  return allResponses.flatMap((response) => response?.success ? response.resData : [])
}

const processSelectedFiles = async () => {
  if (!hasSelectedFiles.value) {
    store.commit('snackbar/showMessage', {
      content: 'Please select at least one file.',
      color: 'warning'
    }, { root: true })

    return
  }

  if (isSizeLimitExceeded.value) {
    store.commit('snackbar/showMessage', {
      content: 'Total file size exceeds the 50MB limit.',
      color: 'error'
    }, { root: true })

    return
  }

  isProcessingWorkflow.value = true
  proxy.$loadingState.start('Fetching selected file content...')

  try {
    const selectedFiles = getSelectedFileObjectsRecursive(fileTree.value)
    const filesToFetch = selectedFiles.map((file) => ({ path: file.path, type: 'blob' }))

    const repoInfo = {
      login: selectedAccount.value.login,
      owner: selectedRepo.value.owner.login,
      repoName: selectedRepo.value.name,
      branch: selectedBranch.value
    }
    const fetchedContent = await fetchFileContents(filesToFetch, repoInfo)

    const contentMap = new Map(fetchedContent.map((file) => [file.path, file.content]))
    selectedFiles.forEach((file) => {
      if (contentMap.has(file.path)) {
        file.content = contentMap.get(file.path)
      }
    })

    selectedFilesWithContent.value = selectedFiles

    const codeSnippets = selectedFilesWithContent.value.filter((file) => isFileRelevantForAnalysis(file.path))
    if (codeSnippets.length === 0) {
      store.commit('snackbar/showMessage', { content: 'No relevant code found in selected files.', color: 'info' }, { root: true })
      return
    }

    proxy.$loadingState.start('Analyzing code...')

    extractedData.value = await buildWorkflowFromCode(codeSnippets, description.value)
    openReviewDialog.value = true
  } catch (error) {
    store.commit('snackbar/showMessage', {
      content: `Error generating workflow: ${error.message}`,
      color: 'error'
    }, { root: true })
  } finally {
    isProcessingWorkflow.value = false
    proxy.$loadingState.stop()
  }
}

const onConfirm = async (workflowSteps) => {
  try {
    const allFileMetadata = repoItems.value.filter((item) => item.type === 'blob')
    const selectedPaths = new Set(selectedFilesWithContent.value.map((file) => file.path))
    const filesToFetch = allFileMetadata.filter((file) => !selectedPaths.has(file.path))
      .map((file) => ({ path: file.path, type: 'blob' }))
    const filesToTag = selectedFilesWithContent.value.filter((file) => isFileRelevantForAnalysis(file.path))
    const selectedFilesToSave = selectedFilesWithContent.value

    openReviewDialog.value = false
    toggleDialog(false)
    proxy.$loadingState.start('Building workflow...')

    const githubInfo = {
      githubAccountLogin: selectedAccount.value.login,
      githubRepo: selectedRepo.value.full_name,
      githubBranch: selectedBranch.value
    }

    const repoInfo = {
      login: selectedAccount.value.login,
      owner: selectedRepo.value.owner.login,
      repoName: selectedRepo.value.name,
      branch: selectedBranch.value
    }
    const newlyFetchedFiles = await fetchFileContents(filesToFetch, repoInfo)
    const allFilesWithContent = [...selectedFilesToSave, ...newlyFetchedFiles]

    await createWorkflow(workflowSteps, { allFiles: allFilesWithContent, filesToTag }, githubInfo)
  } catch (error) {
    store.commit('snackbar/showMessage', {
      content: `Error generating workflow: ${error.message}`,
      color: 'error'
    }, { root: true })
    await nextTick()
    toggleDialog(true)
  } finally {
    proxy.$loadingState.stop()
  }
}

const setNodeSelectionRecursive = (nodes, selected) => {
  if (!nodes) {
    return
  }

  nodes.forEach((node) => {
    node.selected = selected
    node.indeterminate = false
    if (node.type === 'tree') {
      setNodeSelectionRecursive(node.children, selected)
    }
  })
}

const selectAllFiles = (select) => {
  setNodeSelectionRecursive(fileTree.value, select)
  fileTree.value = [...fileTree.value]
  updateTotalSelectedSize()
}

const resetState = () => {
  fileTree.value = []
  initialFetchDone.value = false
  repoItems.value = []
  description.value = ''
  totalSelectedSize.value = 0
  selectedFilesWithContent.value = []
}
</script>

<style scoped>
.border-x {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.v-card {
  border: 1px solid var(--dark-2);
}

:deep(.v-card-item__content) {
  display: flex !important;
  align-items: center !important;
  column-gap: 12px;
  padding: 12px 16px;
}

.workflow-from-code-title {
  font-size: 20px;
  font-weight: 500;
  color: var(--text-light) !important;
}

.workflow-from-code-card {
  padding: 20px 24px !important;
}

.file-tree-list {
  max-height: 516px;
  overflow-y: auto;
}

.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.rounded-t {
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
}

.rounded-b {
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
}
</style>
